'use client'

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { cn } from '@/lib/utils/utils'
import { formFieldInputClassName } from '@/components/form/formFieldSurface'

/**
 * Text input form field component
 * @template TFormValues - The form values type extending FieldValues
 */
interface InputFormFieldProps<TFormValues extends FieldValues> {
    form: UseFormReturn<TFormValues>
    name: Path<TFormValues>
    disabled?: boolean
    label?: string
    /** Label above field, input always full width (no side-by-side layout on large screens) */
    stackLabel?: boolean
    type?: string
    placeholder?: string
    maxDecimalPlaces?: number
    prefixSymbol?: string
    /** Extra classes on the native input (e.g. register / marketing styling) */
    inputClassName?: string
    /** Extra classes on the label (e.g. `text-center` when stacked) */
    labelClassName?: string
    /** Extra classes on the validation message */
    messageClassName?: string
    /** Use when `label` is omitted (accessibility) */
    ariaLabel?: string
    min?: number
    max?: number
}

export const InputFormField = <TFormValues extends FieldValues>({
    form,
    name,
    disabled,
    label,
    stackLabel,
    type,
    placeholder,
    maxDecimalPlaces,
    prefixSymbol,
    inputClassName,
    labelClassName,
    messageClassName,
    ariaLabel,
    min,
    max
}: InputFormFieldProps<TFormValues>) => {
    const stacked = stackLabel ?? false
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem>
                    <div
                        className={cn(
                            'flex flex-col',
                            label && !stacked && 'lg:flex-row lg:items-center',
                            stacked && label && 'gap-2'
                        )}>
                        {label && (
                            <FormLabel
                                className={cn(
                                    stacked ? 'text-left' : 'mb-2 content-center lg:mb-0 lg:basis-1/2',
                                    labelClassName
                                )}>
                                {label}
                            </FormLabel>
                        )}
                        {prefixSymbol && <span className='mr-2'>{prefixSymbol}</span>}
                        <FormControl className={cn(label && !stacked && 'lg:basis-1/2', 'w-full min-w-0')}>
                            <Input
                                aria-label={ariaLabel}
                                disabled={disabled ? disabled : false}
                                type={type || 'text'}
                                placeholder={placeholder || ''}
                                min={min}
                                max={max}
                                className={cn(formFieldInputClassName, inputClassName)}
                                {...field}
                                {...(type === 'number'
                                    ? {
                                          onChange: (e) => {
                                              const raw = e.target.value
                                              if (raw === '') {
                                                  field.onChange('')
                                                  return
                                              }
                                              const parsed = Number(raw)
                                              if (Number.isNaN(parsed)) return
                                              let next = parsed
                                              if (min !== undefined && next < min) next = min
                                              if (max !== undefined && next > max) next = max
                                              field.onChange(String(next))
                                          }
                                      }
                                    : type === 'text' && maxDecimalPlaces !== undefined
                                      ? {
                                            onChange: (e) => {
                                                const input = e.target
                                                const cursorPosition = input.selectionStart || 0
                                                let value = e.target.value
                                                value = value.replace(/[^0-9.]/g, '')
                                                const [integerPart, decimalPart] = value.split('.')
                                                const formattedIntegerPart = integerPart.replace(
                                                    /\B(?=(\d{3})+(?!\d))/g,
                                                    ','
                                                )
                                                const formattedValue =
                                                    decimalPart !== undefined
                                                        ? `${formattedIntegerPart}.${decimalPart.slice(0, maxDecimalPlaces)}`
                                                        : formattedIntegerPart
                                                field.onChange(formattedValue)

                                                requestAnimationFrame(() => {
                                                    const positionOffset = formattedValue.length - value.length
                                                    input.setSelectionRange(
                                                        cursorPosition + positionOffset,
                                                        cursorPosition + positionOffset
                                                    )
                                                })
                                            },
                                            value: field.value
                                        }
                                      : {})}
                            />
                        </FormControl>
                    </div>
                    <FormMessage className={cn(label && !stacked && 'text-right', messageClassName)} />
                </FormItem>
            )}
        />
    )
}
